import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsPlus } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import { getErrorMessage } from '#lib/errors/errorMessages';
import { formatEuro } from '#lib/numberFormat';
import { useInviteMember } from '#orgFeature/models/org/mutations';
import { useAddSeat } from '#settingsFeature/models/billing/mutations';

const SEAT_PRICE_EUR = 5;

const InviteResult = ({ inviteMutation, result }) => (
	<>
		{inviteMutation.isError && (
			<p className="w-full text-sm text-warning-700">{getErrorMessage(inviteMutation.error)}</p>
		)}
		{result?.mailSent && (
			<p className="w-full text-sm text-success-700">Einladung an {result.email} wurde gesendet.</p>
		)}
		{result && !result.mailSent && (
			<p className="w-full text-sm text-warning-700">
				Einladung an {result.email} wurde angelegt, die E-Mail konnte aber nicht zugestellt werden.
			</p>
		)}
	</>
);

InviteResult.propTypes = { inviteMutation: PropTypes.object.isRequired, result: PropTypes.object };

const SeatsFull = ({ totalSeats, isOwner }) => {
	const addSeatMutation = useAddSeat();

	if (!isOwner) {
		return (
			<span className="text-sm text-caution-700">
				Alle {totalSeats} Plätze belegt. Bitte wenden Sie sich an Ihren Team-Owner, um weitere Plätze
				hinzuzufügen.
			</span>
		);
	}
	return (
		<>
			<span className="flex-1 text-sm text-text">Alle {totalSeats} Plätze belegt.</span>
			<Button size="sm" onClick={() => addSeatMutation.mutate()} loading={addSeatMutation.isPending}>
				<BsPlus className="h-4 w-4" />
				Platz kaufen · {formatEuro(SEAT_PRICE_EUR)}/Monat
			</Button>
			<span className="w-full text-xs text-bgBase-800">Wird anteilig auf der nächsten Rechnung berechnet.</span>
		</>
	);
};

SeatsFull.propTypes = { totalSeats: PropTypes.number.isRequired, isOwner: PropTypes.bool.isRequired };

/** Invite bar at the foot of the member list; seats are a property of that list. */
export const InviteMember = ({ usedSeats, totalSeats, isOwner }) => {
	const [email, setEmail] = useState('');
	const [role, setRole] = useState('member');
	const [result, setResult] = useState(null);
	const inviteMutation = useInviteMember();

	const handleSubmit = (e) => {
		e.preventDefault();
		setResult(null);
		if (!email.trim()) return;

		inviteMutation.mutate(
			{ email: email.trim(), role },
			{
				onSuccess: ({ mailSent }) => {
					setResult({ email, mailSent });
					setEmail('');
					setRole('member');
				},
			},
		);
	};

	return (
		<div className="flex flex-wrap items-center gap-3 border-t border-bgBase-500 bg-bgBase-300 px-5 py-3.5">
			{usedSeats < totalSeats ? (
				<form onSubmit={handleSubmit} className="flex w-full flex-wrap items-center gap-3">
					<TextInput
						type="email"
						aria-label="E-Mail-Adresse"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="name@firma.de einladen"
						required
						className="min-w-[220px] flex-1 bg-white"
					/>
					<Select aria-label="Rolle" value={role} onChange={(e) => setRole(e.target.value)} className="w-36">
						<option value="member">Mitglied</option>
						<option value="admin">Admin</option>
					</Select>
					<Button type="submit" disabled={!email.includes('@')} loading={inviteMutation.isPending}>
						Einladen
					</Button>
					<span className="w-full text-xs text-bgBase-800">
						Neue Mitglieder nutzen den gemeinsamen Pool ohne Limit – Sie können jederzeit eines setzen.
					</span>
				</form>
			) : (
				<SeatsFull totalSeats={totalSeats} isOwner={isOwner} />
			)}
			<InviteResult inviteMutation={inviteMutation} result={result} />
		</div>
	);
};

InviteMember.propTypes = {
	usedSeats: PropTypes.number.isRequired,
	totalSeats: PropTypes.number.isRequired,
	isOwner: PropTypes.bool.isRequired,
};
